import { useAuth } from "@workos-inc/authkit-react";
import { ArrowLeftRight, FileText, HardHat, LogIn, ScanSearch, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { authRedirectState } from "@/lib/auth-return-to";

const features = [
  {
    id: "spec-pilot",
    Icon: FileText,
    title: "SpecPilot",
    description: "Manage specification and submittal workflows with clarity.",
  },
  {
    id: "takeoff-pilot",
    Icon: ScanSearch,
    title: "TakeoffPilot",
    description: "Review takeoffs faster and keep quantity work organized.",
  },
  {
    id: "rev-pilot",
    Icon: ArrowLeftRight,
    title: "RevPilot",
    description: "Track revision changes without losing the project context.",
  },
] as const;

export function SignInScreen() {
  const { signIn, signUp } = useAuth();

  return (
    <div className="relative min-h-dvh overflow-hidden bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-5rem] top-[-6rem] size-72 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute bottom-[-8rem] right-[-5rem] size-80 rounded-full bg-accent/15 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] max-w-6xl items-center">
        <Card className="w-full overflow-hidden py-0">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr]">
            <section className="border-b-2 border-border bg-muted/30 p-8 sm:p-10 lg:border-r-2 lg:border-b-0 lg:p-12">
              <div className="flex h-full flex-col justify-between gap-10">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-3 rounded-full border-2 border-border bg-background/80 px-4 py-2 text-[11px] font-bold uppercase tracking-eyebrow text-muted-foreground shadow-sm backdrop-blur">
                    <HardHat className="size-4 text-primary" />
                    Construction Cortex
                  </div>

                  <div className="space-y-5">
                    <img
                      src="/ConstructionCortexLogo-v1.png"
                      alt="Construction Cortex"
                      className="h-14 w-auto max-w-full object-contain sm:h-16"
                    />
                    <div className="space-y-3">
                      <h1 className="max-w-2xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                        One workspace for specs, takeoffs, and revisions.
                      </h1>
                      <p className="max-w-xl text-base leading-7 text-muted-foreground">
                        Construction Cortex brings your core preconstruction workflows together in
                        one place so teams can move from review to delivery with less friction.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  {features.map(({ id, Icon, title, description }) => (
                    <div
                      key={id}
                      className="rounded-lg border-2 border-border bg-background/80 p-4 shadow-sm backdrop-blur"
                    >
                      <Icon className="mb-3 size-5 text-primary" />
                      <p className="text-sm font-semibold text-foreground">{title}</p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="flex items-center p-8 sm:p-10 lg:p-12">
              <div className="w-full space-y-8">
                <div className="space-y-3">
                  <p className="text-sm font-bold uppercase tracking-eyebrow text-muted-foreground">
                    Welcome Back
                  </p>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold tracking-tight text-foreground">
                      Sign in to Construction Cortex
                    </h2>
                    <p className="text-base leading-7 text-muted-foreground">
                      Continue with your company account to access SpecPilot, TakeoffPilot,
                      and RevPilot.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 rounded-lg border-2 border-border bg-muted/30 p-4 shadow-sm">
                  <Button
                    onClick={() => void signIn({ state: authRedirectState() })}
                    className="w-full"
                    size="lg"
                  >
                    <LogIn className="size-4" />
                    Sign In
                  </Button>
                  <div className="space-y-2 border-t-2 border-border pt-4">
                    <p className="text-center text-sm leading-6 text-muted-foreground">
                      New to Construction Cortex? Create an account and set up your company, then start your subscription.
                    </p>
                    <Button
                      onClick={() => void signUp({ state: authRedirectState() })}
                      className="w-full"
                      size="lg"
                      variant="outline"
                    >
                      <UserPlus className="size-4" />
                      Create Account
                    </Button>
                  </div>
                  <p className="text-center text-sm leading-6 text-muted-foreground">
                    Invited by your company? Open the link in your invitation email to join it.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
}
